import { Module, MutationTree, GetterTree, ActionTree } from 'vuex';
import { RootState } from '../types';
import { CheckoutState, CheckoutStepState } from './types';
import { isNil } from 'lodash';
import {
    SetTransferTripAirportDataPayload,
    TransferTrip,
    TransferTripStatus,
} from '@/helpers/transfer/transfer.interfaces';
import { logStatsigCheckoutTransferAvailableTrips } from '@/tracking/statsig/checkout.functions';

const initialState: CheckoutState = {
    step: null,
    states: [],
    clearSessionCart: false,
    transfer: {
        trips: [],
    },
};

const state: CheckoutState = {
    ...initialState,
};

const getters: GetterTree<CheckoutState, RootState> = {
    getClearSessionCart(state): boolean {
        return state.clearSessionCart;
    },
    getStep(state) {
        return state.step;
    },
    getStates(state) {
        return state.states;
    },
    getValidStates(state) {
        return state.states.filter((x) => x.valid).map((x) => x.id) as string[];
    },
    getTransferTrips(state): TransferTrip[] {
        return state.transfer.trips ?? [];
    },
    getTripById: (state) => (tripId: string) => {
        return state.transfer.trips.find((x) => x.id === tripId) ?? [];
    },
};

const actions: ActionTree<CheckoutState, RootState> = {
    setStep({ commit }, payload: string) {
        commit('setStep', payload);
    },
    setStates({ commit }, payload: CheckoutStepState[]) {
        commit('setStates', payload);
    },
    deleteState({ state, commit }, payload: string) {
        const states = [...state.states].filter((s) => s.id !== payload);
        commit('setStates', states);
    },
    setValid({ state, commit }, payload: { id: string; valid: boolean }) {
        const states = [...state.states];
        const index = states.findIndex((item) => item.id === payload.id);

        if (index !== -1) {
            states[index] = { ...states[index], valid: payload.valid };
            commit('setStates', states);
        }
    },
    setTouchedStep({ state, commit }, payload: string) {
        const states = [...state.states];
        const index = states.findIndex((item) => item.id === payload);

        if (index !== -1 && !states[index].touched) {
            states[index] = { ...states[index], touched: true };
            commit('setStates', states);
        }
    },
    setTouchedSteps({ state, commit }, payload: string[]) {
        const states = [...state.states];

        payload.forEach((id) => {
            const index = states.findIndex((item) => item.id === id);

            if (index !== -1 && !states[index].touched) {
                states[index] = { ...states[index], touched: true };
            }
        });

        commit('setStates', states);
    },
    setTouchedField({ state, commit }, payload: { id: string; name: string; value?: any }) {
        const states = [...state.states];
        const index = states.findIndex((item) => item.id === payload.id);

        if (index !== -1) {
            const fields = [...states[index].fields];
            const fieldIndex = fields.findIndex((x) => x.name === payload.name);

            if (fieldIndex === -1) {
                fields.push({
                    name: payload.name,
                    touched: true,
                    ...(!isNil(payload.value) && { value: payload.value }),
                });
            } else {
                fields[fieldIndex] = {
                    ...fields[fieldIndex],
                    touched: true,
                    ...(!isNil(payload.value) && { value: payload.value }),
                };
            }

            states[index] = {
                ...states[index],
                fields: fields,
            };

            commit('setStates', states);
        }
    },
    setClearSessionCart({ commit }, payload: boolean) {
        commit('setClearSessionCart', payload);
    },

    setTransferTrips({ commit }, payload: TransferTrip[]) {
        if (payload.length) {
            logStatsigCheckoutTransferAvailableTrips(payload);
        }

        commit('setTransferTrips', payload);
    },

    setTransferTripStatus(
        { state, commit },
        payload: { id: string; status: TransferTripStatus }
    ) {
        const updatedTrips = state.transfer.trips.map((x) => ({
            ...x,
            status: x.id === payload.id ? payload.status : x.status,
        }));

        commit('setTransferTrips', updatedTrips);
    },

    setTransferTripActive({ state, commit }, payload: { id: string; active: boolean }) {
        const updatedTrips = state.transfer.trips.map((x) => ({
            ...x,
            active: x.id === payload.id ? payload.active : x.active,
        }));

        commit('setTransferTrips', updatedTrips);
    },

    setTransferTripStartTime({ state, commit }, payload: { id: string; startTime: string }) {
        const updatedTrips = state.transfer.trips.map((x) => ({
            ...x,
            request:
                x.id === payload.id
                    ? { ...x.request, startTime: payload.startTime }
                    : x.request,
        }));

        commit('setTransferTrips', updatedTrips);
    },

    setTransferTripAirportData(
        { state, commit },
        payload: SetTransferTripAirportDataPayload
    ) {
        const updatedTrips = state.transfer.trips.map((trip) => {
            if (trip.id !== payload.id) return trip;

            const updatedRequest = payload.request
                ? { ...trip.request, ...payload.request }
                : trip.request;

            const updatedPoints: Partial<{ from: typeof trip.from; to: typeof trip.to }> = {};
            const pointKeys: Array<'from' | 'to'> = ['from', 'to'];

            pointKeys.forEach((key) => {
                if (payload[key]) {
                    updatedPoints[key] = {
                        ...trip[key],
                        ...payload[key],
                        location: {
                            ...trip[key].location,
                            ...payload[key].location,
                        },
                    };
                }
            });

            return {
                ...trip,
                request: updatedRequest,
                ...updatedPoints,
            };
        });

        commit('setTransferTrips', updatedTrips);
    },
};

const mutations: MutationTree<CheckoutState> = {
    setStep(state, payload) {
        state.step = payload;
    },
    setStates(state, payload) {
        state.states = payload;
    },
    setClearSessionCart(state, payload) {
        state.clearSessionCart = payload;
    },
    setTransferTrips(state, payload) {
        state.transfer.trips = payload;
    },
};

export const checkout: Module<CheckoutState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
